import { ReactNode } from 'react';

import {
   MedicalInformationCountByTypeQuery,
   MedicalInformationLinkedToMedicalEventFault,
   MedicalInformationLinkedToVisitFault,
   MedicalInformationMarkAsRemovedCommand,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { useTranslate } from '@dreryk/react-common';
import { ActionEventInterceptor, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';

import {
   PatientMedicalInformationRemoveFormBean,
   PatientMedicalInformationRemoveFormController,
} from '../PatientMedicalInformationRemoveForm.types';
import { translationKeys } from '../../translations';

import { getDescription } from './MedicalInformationMarkAsRemovedActionIntereceptor.utils';

type MedicalInformationMarkAsRemovedActionInterceptorProps = Pick<
   PatientMedicalInformationRemoveFormController,
   'handleSubmit'
> & {
   children: ReactNode;
   medicalInformationId: number;
   onMedicalInformationLinkedToMedicalEventFault: VoidFunction;
   onMedicalInformationLinkedToVisitFault: VoidFunction;
   onSuccess: VoidFunction;
};

export const MedicalInformationMarkAsRemovedActionInterceptor = (
   props: MedicalInformationMarkAsRemovedActionInterceptorProps
): JSX.Element => {
   const translate = useTranslate();

   const { executeAsync: markMedicalInformationAsRemoved } = useCqrsCommand<
      void,
      PatientMedicalInformationRemoveFormBean,
      MedicalInformationLinkedToMedicalEventFault | MedicalInformationLinkedToVisitFault
   >(
      (parameters) => {
         const command = new MedicalInformationMarkAsRemovedCommand();
         command.medicalInformationId = props.medicalInformationId;
         command.reason = getDescription(parameters, translate);
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.medicalInformationHasBeenRemoved);
            refetchCqrsQuery(MedicalInformationCountByTypeQuery);
            props.onSuccess();
         },
         onFault: {
            'egabinet-patient-medical-information-frontend-api:MedicalInformationLinkedToMedicalEventFault':
               props.onMedicalInformationLinkedToMedicalEventFault,
            'egabinet-patient-medical-information-frontend-api:MedicalInformationLinkedToVisitFault':
               props.onMedicalInformationLinkedToVisitFault,
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(markMedicalInformationAsRemoved)}>
         {props.children}
      </ActionEventInterceptor>
   );
};
